// A TAB THAT HAS BEEN OPEN SINCE BEFORE A DEPLOY IS RUNNING CODE NOBODY CAN FIX.
//
// On 1 October, creating a user failed with:
//
//   Access to fetch at 'https://survai-api.onrender.com/api/auth/create-user'
//   from origin 'https://app.survaihealth.com' has been blocked by CORS policy:
//   No 'Access-Control-Allow-Origin' header is present on the requested resource.
//
// Nothing was wrong with the server. The deployed bundle had zero references to that host,
// the current source had zero, there was no service worker, and Netlify serves both
// index.html and the bundle with `max-age=0, must-revalidate`. The browser was simply
// running a bundle loaded BEFORE the backend moved, and had never reloaded since.
//
// The giveaway was a call to /api/email/welcome -- an endpoint that exists in no current
// source file and no deployed asset. Only old JavaScript could have asked for it.
//
// This cost days. The backend moved, every running tab kept talking to the retired host,
// and the failure surfaced only when somebody used a screen that is not used daily. Then
// it looked like a server fault, which is where the time went.
//
// ── WHY NOT ASK THE API WHICH BUILD IS CURRENT ────────────────────────────────
//
// Because the stale tab cannot. It would ask the host it was BUILT with -- the retired one,
// which is suspended and answers nothing. A check that depends on reaching the new backend
// is exactly the check that cannot run in the case it exists for.
//
// So this asks the tab's OWN ORIGIN, which is always reachable and always current: fetch
// index.html, read the hashed bundle filename out of it, and compare with the filename this
// code is actually running from. Create React App content-hashes that name, so a changed
// bundle is a changed hash. Same-origin, no CORS, no server endpoint, no build-pipeline
// change, and it works even when the backend the tab believes in is gone.
//
// ── WHAT IT DOES NOT DO ──────────────────────────────────────────────────────
//
// It never reloads on its own. A forced reload during a half-written plan of correction
// would be a worse bug than the one it fixes, and this app holds long unsaved forms. It
// tells the operator and lets them pick the moment.

/** The bundle filename this code is running from, e.g. "main.6999e28d.js". */
function runningBundle() {
  // document.currentScript is null inside a module and inside async code, so read the DOM.
  // The entry bundle is the one CRA names main.<hash>.js; chunks are named differently.
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  for (const s of scripts) {
    const m = String(s.getAttribute("src") || "").match(/\/(main\.[A-Za-z0-9]+\.js)$/);
    if (m) return m[1];
  }
  return null;
}

/** The bundle filename the server is serving right now. */
async function deployedBundle() {
  // cache: "no-store" rather than a cache-busting query string: a query string makes a
  // distinct URL that some CDNs treat as a separate cacheable object, and Netlify answers
  // the unknown path with the SPA fallback -- which is index.html, so the check would
  // appear to work while measuring nothing.
  const res = await fetch("/index.html", { cache: "no-store", credentials: "omit" });
  if (!res.ok) throw new Error(`index.html -> ${res.status}`);
  const html = await res.text();
  const m = html.match(/\/static\/js\/(main\.[A-Za-z0-9]+\.js)/);
  if (!m) throw new Error("no main bundle referenced in index.html");
  return m[1];
}

/**
 * Watch for the running bundle falling behind the deployed one.
 *
 * @param {(info: {running: string, deployed: string}) => void} onStale
 *        Called once, when a mismatch is first seen. Not called again.
 * @returns {() => void} stop
 */
export function watchForStaleTab(onStale) {
  const running = runningBundle();
  // No filename to compare means this is not a hashed production build -- `npm start`, or a
  // test. Checking would produce a false positive on every dev reload.
  if (!running) return () => {};

  let stopped = false;
  let fired = false;

  const check = async () => {
    if (stopped || fired || typeof document === "undefined") return;
    // Pointless and rude while the tab is in the background; also where browsers throttle
    // timers hardest, which would make the interval meaningless anyway.
    if (document.visibilityState !== "visible") return;
    try {
      const deployed = await deployedBundle();
      if (!stopped && !fired && deployed !== running) {
        fired = true;
        onStale({ running, deployed });
      }
    } catch (_) {
      // Offline, or a transient 5xx. Silent on purpose: an inability to check is not
      // evidence of staleness, and a warning every ten minutes would train people to
      // ignore it.
    }
  };

  // Not on load. A reload prompt in the first second of a session is noise, and the tab
  // that has just loaded is by definition current.
  const EVERY = 10 * 60 * 1000;
  const timer = setInterval(check, EVERY);
  // And when the operator comes back to the tab, which is when a long-idle tab -- the one
  // this exists for -- becomes relevant again.
  //
  // RETURNS the promise rather than discarding it. Nothing in the browser awaits a
  // visibilitychange handler, but a test must, and a handler that swallows its promise can
  // only be tested with a sleep -- which is how a check that fires one tick late gets
  // written off as flaky instead of fixed.
  const onVisible = () =>
    document.visibilityState === "visible" ? check() : Promise.resolve();
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export const _internals = { runningBundle, deployedBundle };
